import { type ReactNode } from 'react';
import { Modal } from '@/components/ui/modal';
import { Fade } from '@/components/ui/fade';
import { AnimatedHeight } from '@/components/ui/animated-height';
import { EmptyState } from '@/components/ui/empty-state';
import { useMinLoading } from '@/hooks/use-min-loading';
import { BonusHistorySkeleton } from './bonus-history-skeleton';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  loading: boolean;
  empty: boolean;
  emptyLabel?: string;
  children: ReactNode;
};

type State = 'skeleton' | 'empty' | 'data';

function pickState(p: Props, showSkeleton: boolean): State {
  if (p.loading || showSkeleton) return 'skeleton';
  if (p.empty) return 'empty';
  return 'data';
}

function StateView({
  state,
  emptyLabel,
  children,
}: {
  state: State;
  emptyLabel: string;
  children: ReactNode;
}): ReactNode {
  if (state === 'skeleton') return <BonusHistorySkeleton />;
  if (state === 'empty') return <EmptyState title={emptyLabel} />;
  return <div className="-mx-base overflow-x-auto px-base">{children}</div>;
}

export function BonusHistoryModal(p: Props): ReactNode {
  const showSkeleton = useMinLoading(p.loading);
  const state = pickState(p, showSkeleton);
  return (
    <Modal open={p.open} onClose={p.onClose} title={p.title} size="lg">
      <AnimatedHeight>
        <Fade activeKey={state}>
          <StateView
            state={state}
            emptyLabel={p.emptyLabel ?? 'Sin datos todavía'}
          >
            {p.children}
          </StateView>
        </Fade>
      </AnimatedHeight>
    </Modal>
  );
}

export function HistoryButton({ onClick }: { onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[10px] text-text-tertiary transition-colors hover:text-text-primary"
    >
      Ver hist&oacute;rico
    </button>
  );
}
